import {
  CourseValidationErrors,
  PromptErrorsArray,
} from "@src/features/libraryItem/course/types/validation";
import { AiRequestFragment } from "@src/features/libraryItem/course/hooks/useCourseVersionAIRequests.generated";
import { PromptType } from "@src/types.generated";
import {
  CourseContentLessonFragment,
  CourseContentPromptFragment,
} from "../build/CourseBuildContent.generated";
import { CommentThreadFragment } from "../build/comments/Comment.generated";

type BaseScreen = {
  id: string;
  index: number;
};

export type PromptScreenAttrs = {
  prompt: CourseContentPromptFragment;
  lesson: Pick<CourseContentLessonFragment, "uuid">;
  promptPositionInLesson: number;
  /** 1-based position among the lesson's question prompts; 0 for non-questions. */
  questionNumber: number;
  questionCount: number;
  validationErrors: PromptErrorsArray;
  isGeneratedByAI: boolean;
  commentThread: CommentThreadFragment | undefined;
  aiRequests: AiRequestFragment[] | undefined;
};

export type PromptScreen = BaseScreen & {
  promptScreen: PromptScreenAttrs;
};

export type LessonScreenAttrs = {
  // Not the whole lesson: its prompts change on every edit in the lesson, and
  // the lesson screen would re-render with them.
  lesson: Pick<CourseContentLessonFragment, "uuid" | "name">;
  index: number;
  hasEmptyLessonValidationErrors: boolean;
};

export type LessonScreen = BaseScreen & {
  lessonScreen: LessonScreenAttrs;
};

export type HomeScreen = BaseScreen & {
  // eslint-disable-next-line @typescript-eslint/ban-types
  homeScreen: {};
};

export type CourseScreen = PromptScreen | LessonScreen | HomeScreen;

export type BuildCourseScreensArgs = {
  lessons: CourseContentLessonFragment[] | undefined;
  validationErrors?: CourseValidationErrors;
  promptUuidsGeneratedByAI?: Set<string>;
  commentThreads?: CommentThreadFragment[];
  aiRequests?: AiRequestFragment[];
};

export const buildCourseScreens = (
  args: BuildCourseScreensArgs,
): { screens: CourseScreen[]; screensMap: Map<string, CourseScreen> } => {
  const promptUuidToCommentThreadMap = new Map<string, CommentThreadFragment>();
  args.commentThreads?.forEach((ct) => {
    if (ct.prompt) {
      promptUuidToCommentThreadMap.set(ct.prompt.uuid, ct);
    }
  });
  const promptUuidToAIRequestsMap = new Map<string, AiRequestFragment[]>();
  args.aiRequests?.forEach((aiReq) => {
    if (aiReq.aiEditPromptRequest?.prompt) {
      const uuid = aiReq.aiEditPromptRequest.prompt.uuid;
      const array = promptUuidToAIRequestsMap.get(uuid) || [];
      array.push(aiReq);
      promptUuidToAIRequestsMap.set(uuid, array);
    }
  });
  const screens: CourseScreen[] = [];
  const screensMap = new Map<string, CourseScreen>();
  const courseVersionLessons = args.lessons;
  if (!courseVersionLessons) return { screens, screensMap };
  const homeScreen: CourseScreen = {
    index: 0,
    id: getCourseHomeScreenId(),
    homeScreen: {},
  };
  screens.push(homeScreen);
  screensMap.set(homeScreen.id, homeScreen);
  let index = 1;
  for (
    let lessonIdx = 0;
    lessonIdx < courseVersionLessons.length;
    lessonIdx++
  ) {
    const lesson = courseVersionLessons[lessonIdx];
    if (courseVersionLessons.length > 1) {
      const lessonScreen: CourseScreen = {
        index: index,
        id: getLessonScreenId(lesson.uuid),
        lessonScreen: {
          lesson: { uuid: lesson.uuid, name: lesson.name },
          index: lessonIdx,
          hasEmptyLessonValidationErrors:
            !!args.validationErrors?.emptyLessons.has(lesson.uuid),
        },
      };
      screens.push(lessonScreen);
      screensMap.set(lessonScreen.id, lessonScreen);
      index++;
    }
    const lessonRef = { uuid: lesson.uuid };
    const questionUuids = lesson.prompts
      .filter(
        (p) =>
          p.type !== PromptType.Message && p.type !== PromptType.TitleScreen,
      )
      .map((p) => p.uuid);
    for (let promptIdx = 0; promptIdx < lesson.prompts.length; promptIdx++) {
      const prompt = lesson.prompts[promptIdx];
      const promptScreen: CourseScreen = {
        index: index,
        id: getPromptScreenId(prompt.uuid),
        promptScreen: {
          prompt,
          lesson: lessonRef,
          questionNumber: questionUuids.indexOf(prompt.uuid) + 1,
          questionCount: questionUuids.length,
          validationErrors:
            args.validationErrors?.promptErrors.get(prompt.uuid) || [],
          isGeneratedByAI:
            args.promptUuidsGeneratedByAI?.has(prompt.uuid) || false,
          commentThread: promptUuidToCommentThreadMap.get(prompt.uuid),
          aiRequests: promptUuidToAIRequestsMap.get(prompt.uuid),
          promptPositionInLesson: promptIdx,
        },
      };
      screens.push(promptScreen);
      screensMap.set(promptScreen.id, promptScreen);
      index++;
    }
  }
  return { screens, screensMap };
};

export const getCourseHomeScreenId = (): string => "course-home";
export const getLessonScreenId = (lessonUuid: string): string =>
  `lesson-${lessonUuid}`;
export const getPromptScreenId = (promptUuid: string): string =>
  `prompt-${promptUuid}`;
